<template>
  <div class="nav-section scrollable" v-if="displayCategories.length > 0">
    <div class="section-label" v-if="props.isOpen">
      <span>{{ t('menu.ServicesAndTools') }}</span>
      <div class="section-divider"></div>
    </div>

    <div
      class="categories-container"
      :class="{
        'is-categories-container-closed': !props.isOpen,
      }"
    >
      <div
        v-for="category in displayCategories"
        :key="category.id"
        class="category-group"
        :class="{
          'is-active': activeCategory === category.id,
          'has-items': category.links && category.links.length > 0,
          'is-drawer-closed': !props.isOpen,
        }"
      >
        <div class="category-tooltip" v-if="!props.isOpen">
          {{ getCategoryName(category) }}
        </div>
        <div class="category-header" @click="toggleCategory(category)">
          <div class="category-icon">
            <IconRenderer
              :name="getCategoryIcon(category)"
              size="small"
              :isActive="activeCategory === category.id"
              :activeCssColor="'var(--ion-text-color-contrast)'"
            />
          </div>

          <div class="category-content" v-if="props.isOpen">
            <div class="category-title">{{ getCategoryName(category) }}</div>
            <div class="category-count">
              {{ category.links?.length || 0 }} {{ t('menu.links') }}
            </div>
          </div>

          <div class="category-toggle" v-if="props.isOpen && category.links?.length">
            <IconRenderer
              :name="activeCategory === category.id ? 'chevron-down' : 'chevron-down'"
              size="small"
              class="toggle-icon"
            />
          </div>
        </div>

        <!-- Category Links -->
        <div class="category-links" v-if="activeCategory === category.id && props.isOpen">
          <ModuleLink
            v-for="link in category.links"
            :key="link.id"
            :link="link"
            @click="handleLinkClick"
          />
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, computed } from 'vue';
import { useModuleApi } from '@/features/menu-management/composables/useModuleApi';
import { useUIStore } from '@/common/stores/uiStore';
import { useLocale } from '@/common/composables/useLocale';
import { ModulesNamesFR, ModulesNamesEN } from '@/features/menu-management/constants/menuConstants';
import { NavigationModuleWithLinks } from '@/features/menu-management/types/menuTypes';
import IconRenderer from '@/common/components/ui/icon/IconRenderer.vue';
import ModuleLink from './ModuleLink.vue';

interface Props {
  isActive?: boolean;
  isOpen?: boolean;
}

const props = defineProps<Props>();

const emit = defineEmits<{
  (e: 'link-click'): void;
}>();

// Composables
const { modulesWithLinks } = useModuleApi();
const uiStore = useUIStore();
const { t, currentLocale } = useLocale();

// Reactive state
const activeCategory = ref<number | null>(null);

const displayCategories = computed(() => {
  const visibleModules = modulesWithLinks.value.filter(
    (module) => !module.hidden && module.links && module.links.length > 0
  );
  const sortedModules = [...visibleModules].sort((a, b) => a.order - b.order);
  return sortedModules;
});

function getCategoryIcon(category: NavigationModuleWithLinks): string {
  const iconMappings: [string[], string][] = [
    [[ModulesNamesFR.MyAcademicLife, ModulesNamesEN.MyAcademicLife], 'school-outline'],
    [[ModulesNamesFR.MyProfessionalLife, ModulesNamesEN.MyProfessionalLife], 'business-outline'],
    [[ModulesNamesFR.MyInternationalLife, ModulesNamesEN.MyInternationalLife], 'airplane-outline'],
    [[ModulesNamesFR.Administrative, ModulesNamesEN.Administrative], 'document-text-outline'],
    [[ModulesNamesFR.PracticalLife, ModulesNamesEN.PracticalLife], 'home-outline'],
    [[ModulesNamesFR.ListeningAndHealth, ModulesNamesEN.ListeningAndHealth], 'heart-outline'],
  ];

  for (const [keys, icon] of iconMappings) {
    if (keys.includes(category.name)) {
      return icon;
    }
  }
  return 'folder-outline';
}

function getCategoryName(category: NavigationModuleWithLinks): string {
  const locale = currentLocale.value;

  if (category.nameTranslations && category.nameTranslations.length > 0) {
    const translation = category.nameTranslations.find((trans) =>
      trans.culture.toLowerCase().startsWith(locale.toLowerCase())
    );
    if (translation) {
      return translation.value;
    }
  }

  return category.name;
}

function toggleCategory(category: NavigationModuleWithLinks) {
  if (!props.isOpen) {
    uiStore.openDrawer();
    activeCategory.value = category.id;
    return;
  }

  if (activeCategory.value === category.id) {
    activeCategory.value = null;
  } else {
    activeCategory.value = category.id;
  }
}

function handleLinkClick() {
  emit('link-click');
}
</script>

<style scoped>
.nav-section {
  padding: 4px;
  border-bottom: 1px solid rgba(var(--ion-border-color-rgb), 0.05);
  position: relative;
}

.nav-section:last-child {
  border-bottom: none;
}

.section-label {
  display: flex;
  align-items: center;
  gap: 12px;
  margin-bottom: 16px;
  margin-top: 16px;
  animation: slideInLeft 0.4s ease-out 0.1s both;
}

.section-label span {
  font-size: 11px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.8px;
  color: var(--ion-color-medium);
  white-space: nowrap;
}

.section-divider {
  flex: 1;
  height: 1px;
  background: linear-gradient(90deg, rgba(var(--ion-color-medium-rgb), 0.3), transparent);
}

/* Categories */
.categories-container {
  display: flex;
  flex-direction: column;
  gap: 2px;
}

.category-group {
  border-radius: 12px;
  transition: all 0.3s ease;
}

.category-group.is-active {
  background: rgba(var(--ion-color-primary-rgb), 0.03);
}

.category-header {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 12px;
  cursor: pointer;
  transition: all 0.3s ease;
  position: relative;
}

.category-header:hover {
  background: rgba(var(--ion-color-primary-rgb), 0.05);
  border-radius: 0 2vmin 2vmin 0;
}

.category-icon {
  width: 33px;
  height: 33px;
  background: rgba(var(--ion-color-primary-rgb), 0.1);
  border-radius: 10px;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.3s ease;
  border: 1px solid rgba(var(--ion-color-primary-rgb), 0.2);
  position: relative;
}

.category-group.is-active .category-icon {
  background: var(--ion-color-primary);
}

.category-tooltip {
  position: absolute;
  left: 60px;
  transform: translateY(50%);
  padding: 6px 10px;
  width: max-content;
  border-radius: 3vmin;
  background-color: var(--ion-color-primary);
  color: var(--ion-text-color-contrast);
  opacity: 0;
  z-index: 9999;
  font-size: 12px;
}

.global-category-tooltip {
  position: absolute;
  z-index: 99999;
  background: var(--ion-background-color);
  color: var(--ion-text-color);
  border-radius: 8px;
  padding: 6px 10px;
  font-size: 12px;
  box-shadow: var(--ion-box-shadow-hover);
  white-space: nowrap;
  pointer-events: none;
}

.category-group.is-drawer-closed:hover .category-tooltip {
  opacity: 1;
}

.category-content {
  flex: 1;
  min-width: 0;
  animation: slideInLeft 0.4s ease-out;
}

.category-title {
  font-size: 14px;
  font-weight: 500;
  color: var(--ion-text-color);
  line-height: 1.2;
  margin-bottom: 2px;
}

.category-count {
  font-size: 11px;
  color: var(--ion-color-medium);
  line-height: 1.2;
}

.category-toggle {
  transition: transform 0.3s ease;
}

.category-group.is-active .category-toggle {
  transform: rotate(180deg);
}

.is-categories-container-closed {
  display: flex;
  flex-direction: column;
  align-items: center;
  margin-top: 60px;
}

.category-group.is-drawer-closed .category-header {
  justify-content: center;
}

.category-links {
  padding: 8px 12px 12px 20px;
  animation: slideDown 0.3s ease-out forwards;
  overflow: hidden;
}

@keyframes slideDown {
  from {
    opacity: 0;
    transform: translateY(-8px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

/* Animations */
@keyframes slideInLeft {
  from {
    opacity: 0;
    transform: translateX(-16px);
  }
  to {
    opacity: 1;
    transform: translateX(0);
  }
}
</style>
